import { PlanCodes } from 'constants/api/planCodes';
import React, { useEffect, useMemo, useState } from 'react';
import { MutationResult } from '@apollo/client';
import {
    Box,
    Heading,
    Text,
    Button,
    CheckCircleOutlined,
} from '@landr/maestro';
import { ActivateTrialSubscription } from 'apollo/user';
import { Trans } from '@lingui/macro';
import { getFormattedPrice } from '@landr/pricing';
import { SubscriptionsAndAllowedFeaturesQuery } from 'types/generated/graphql';

type ActivateTrialSubscriptionModalContentProps = {
    trialingSubscription:
        | ArrayElement<
              NonNullable<
                  SubscriptionsAndAllowedFeaturesQuery['subscriptionsAndAllowedFeatures']
              >['subscriptionsDetails']
          >
        | undefined;
    loading: MutationResult<typeof ActivateTrialSubscription>['loading'];
    error: MutationResult<typeof ActivateTrialSubscription>['error'];
    data: MutationResult<typeof ActivateTrialSubscription>['data'];
    onClose(): void;
    onActivateNow(): void;
};

export const ActivateTrialSubscriptionModalContent: React.FC<
    ActivateTrialSubscriptionModalContentProps
> = ({
    trialingSubscription,
    loading,
    error,
    data,
    onClose,
    onActivateNow,
}) => {
    const [isSubscriptionActivated, setIsSubscriptionActivated] =
        useState(false);
    const planName = PlanCodes[trialingSubscription?.current.code || ''];

    const nextPaymentFormattedPrice = useMemo(() => {
        const nextPaymentAmountValue =
            trialingSubscription?.current.nextPaymentAmount.value;

        if (nextPaymentAmountValue) {
            return getFormattedPrice({
                value: nextPaymentAmountValue,
                currency:
                    trialingSubscription?.current.nextPaymentAmount.currency,
            });
        }
    }, [trialingSubscription]);

    useEffect(() => {
        if (data) {
            // No point in using the content of data here, since Stripe could take up to 1 hour
            //  to trigger the webhook that will charge the user.
            // However, Stripe documentation states the webhook is triggered "now".
            setIsSubscriptionActivated(!!data);
        }
    }, [data]);

    if (error) {
        return (
            <>
                <Heading as="h5" textStyle="title-sm" mb="none">
                    <Trans>Try again</Trans>
                </Heading>
                <Box>
                    <Text color="text.medium">
                        <Trans>An error has occurred. Please try again.</Trans>
                    </Text>
                </Box>
                <Box>
                    <Button onClick={onClose} isRounded>
                        <Trans>Got it</Trans>
                    </Button>
                </Box>
            </>
        );
    }

    if (isSubscriptionActivated) {
        return (
            <>
                <CheckCircleOutlined color="success.300" size="xl" />
                <Heading as="h5" textStyle="title-sm" mb="none">
                    <Trans>{`You're all set!`}</Trans>
                </Heading>
                <Box>
                    <Text color="text.medium">
                        <Trans>
                            You will soon have full access to {planName}.
                        </Trans>
                    </Text>
                </Box>
                <Box>
                    <Button onClick={onClose} isLoading={loading} isRounded>
                        <Trans>Got it</Trans>
                    </Button>
                </Box>
            </>
        );
    }

    return (
        <>
            <Heading as="h5" textStyle="title-sm" mb="none">
                <Trans>Activate your plan to access this feature</Trans>
            </Heading>
            <Box>
                <Text color="text.medium">
                    <Trans>
                        You're still on your free trial. Want to get full{' '}
                        {planName} features? It's easy!
                    </Trans>
                </Text>
            </Box>
            <Box>
                <Button onClick={onActivateNow} isLoading={loading} isRounded>
                    <Trans>Activate now</Trans>
                </Button>
            </Box>
            {trialingSubscription?.isYearly && nextPaymentFormattedPrice && (
                <Text pt="sm" color="text.medium" textStyle="body-sm">
                    <Trans>
                        *You’ll be charged {nextPaymentFormattedPrice} upon
                        activation.
                    </Trans>
                </Text>
            )}
        </>
    );
};
